
import React, { useEffect, useState } from "react";

const COLLECTION_API =
  "https://api.vanyacollectionsrajahmundry.com/categories";
const PRODUCTS_API =
  "https://api.vanyacollectionsrajahmundry.com/products";

const normalizeCategoryKey = (val) =>
  String(val || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);

  const [categoryName, setCategoryName] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingOriginalName, setEditingOriginalName] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  // ============================================================
  // FETCH CATEGORIES
  // ============================================================

  const fetchCategories = async () => {
    try {
      setLoading(true);

      const response = await fetch(COLLECTION_API);

      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }

      const data = await response.json();

      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("FETCH CATEGORY ERROR:", error);
      setMessage("Failed to load categories.");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    fetchCategories();
  }, []);

  // ============================================================
  // IMAGE SELECT
  // ============================================================

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setImageFile(file);
    setMessage("");

    // Only for preview
    const reader = new FileReader();

    reader.onloadend = () => {
      setImagePreview(reader.result);
    };

    reader.readAsDataURL(file);
  };

  // ============================================================
  // SYNCHRONIZE INVENTORY PRODUCTS WHEN CATEGORY NAME CHANGES
  // ============================================================

  const syncCategoryProductsInInventory = async (oldCatName, newCatName) => {
    const cleanNew = String(newCatName || "").trim();
    const cleanOld = String(oldCatName || "").trim();
    if (!cleanNew) return 0;

    const normNew = normalizeCategoryKey(cleanNew);
    const normOld = normalizeCategoryKey(cleanOld);

    try {
      // Persist rename mapping in localStorage so any open tabs/components can reconcile
      try {
        const existingMapRaw = localStorage.getItem("vanya_category_rename_map");
        const renameMap = existingMapRaw ? JSON.parse(existingMapRaw) : {};
        if (normOld) renameMap[normOld] = cleanNew;
        if (normNew) renameMap[normNew] = cleanNew;
        localStorage.setItem("vanya_category_rename_map", JSON.stringify(renameMap));
      } catch (e) {
        // ignore storage errors
      }

      const res = await fetch(`${PRODUCTS_API}/all?admin=true`);
      if (!res.ok) return 0;
      const allProducts = await res.json();
      if (!Array.isArray(allProducts)) return 0;

      const productsToUpdate = allProducts.filter((p) => {
        const prodCat = String(p.category || "").trim();
        const normProdCat = normalizeCategoryKey(prodCat);
        if (!normProdCat) return false;
        const matchesOld = normOld && normProdCat === normOld;
        const matchesNewKey = normNew && normProdCat === normNew;
        return (matchesOld || matchesNewKey) && prodCat !== cleanNew;
      });

      let updatedCount = 0;

      for (const p of productsToUpdate) {
        try {
          const fd = new FormData();
          fd.append("name", p.name || "");
          fd.append("cat", cleanNew);
          fd.append("subCategory", p.sub_category || "");
          fd.append("price", p.price ?? 0);
          fd.append("oldPrice", p.old_price ?? 0);
          fd.append("stock", p.stock ?? 0);
          fd.append("type", p.type || "Regular");
          fd.append("fabric", p.fabric || "");
          fd.append("length", p.length || "");
          fd.append("occasion", p.occasion || "");
          fd.append("authenticity", p.authenticity || "");
          if (p.is_hold !== undefined && p.is_hold !== null) {
            fd.append("is_hold", String(Boolean(p.is_hold)));
          }

          const rawVariants = Array.isArray(p.variants) ? p.variants : [];
          const fallbackMainImg =
            p.img_url || rawVariants[0]?.mainImage || rawVariants[0]?.main_image || "";
          if (fallbackMainImg && !String(fallbackMainImg).startsWith("blob:")) {
            fd.append("existingMainImage", fallbackMainImg);
          }

          const existingThumbs = Array.isArray(p.thumbnails)
            ? p.thumbnails.filter(
                (u) => typeof u === "string" && u.trim() !== "" && !u.startsWith("blob:")
              )
            : [];
          fd.append("existingThumbnails", JSON.stringify(existingThumbs));

          const variantsPayload = rawVariants.map((v, idx) => {
            const vMain =
              v.mainImage || v.main_image || v.existingMainImage || "";
            const vThumbs = Array.isArray(v.thumbnails)
              ? v.thumbnails.filter(
                  (u) => typeof u === "string" && u.trim() !== "" && !u.startsWith("blob:")
                )
              : [];
            const base = {
              colour: v.colour || v.color || "",
              existingMainImage: !String(vMain).startsWith("blob:") ? vMain : "",
              existingThumbnails: vThumbs,
              mainImageField: "",
              thumbnailField: `variant_${idx}_thumbnails`,
            };
            if (Array.isArray(v.sizes)) {
              return {
                ...base,
                sizes: v.sizes.map((s) => ({
                  size: s.size || "",
                  price: Number(s.price || 0),
                  oldPrice: Number(s.oldPrice || s.old_price || 0),
                  discount: Number(s.discount || 0),
                  stock: Number(s.stock || 0),
                })),
              };
            }
            return {
              ...base,
              price: Number(v.price || 0),
              oldPrice: Number(v.oldPrice || v.old_price || 0),
              discount: Number(v.discount || 0),
              stock: Number(v.stock || 0),
            };
          });

          const variantImageMeta = variantsPayload.map((v, idx) => ({
            colourIndex: idx,
            colour: v.colour || "",
            mainImageField: "",
            existingMainImage: v.existingMainImage || "",
            thumbnailField: `variant_${idx}_thumbnails`,
            existingThumbnails: v.existingThumbnails || [],
          }));

          fd.append("variants", JSON.stringify(variantsPayload));
          fd.append("variantImageMeta", JSON.stringify(variantImageMeta));

          const updateRes = await fetch(`${PRODUCTS_API}/update/${p.id}`, {
            method: "PUT",
            body: fd,
          });
          if (updateRes.ok) {
            updatedCount++;
          }
        } catch (prodErr) {
          console.error(`Failed to sync category for product #${p.id}:`, prodErr);
        }
      }

      return updatedCount;
    } catch (err) {
      console.error("Error syncing category products:", err);
      return 0;
    }
  };

  // ============================================================
  // ADD / UPDATE CATEGORY
  // ============================================================

  const handleSave = async () => {
    // Validate category name
    if (!categoryName.trim()) {
      setMessage("Please enter category name.");
      return;
    }

    // Image is required only when ADDING
    if (editingId === null && !imageFile) {
      setMessage("Please upload an image.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");

      const formData = new FormData();

      // Category name
      formData.append(
        "category",
        categoryName.trim()
      );

      // Image
      // For ADD: required
      // For UPDATE: optional
      if (imageFile) {
        formData.append("image", imageFile);
      }

      let url;
      let method;

      // ========================================================
      // ADD
      // ========================================================

      if (editingId === null) {
        url = `${COLLECTION_API}/add`;
        method = "POST";
      }

      // ========================================================
      // UPDATE
      // ========================================================

      else {
        url = `${COLLECTION_API}/update/${editingId}`;
        method = "PUT";
      }

      console.log("CATEGORY REQUEST:", {
        method,
        url,
        editingId,
        category: categoryName.trim(),
        hasImage: !!imageFile,
      });

      const response = await fetch(url, {
        method,
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            (editingId !== null
              ? "Failed to update category."
              : "Failed to add category.")
        );
      }

      // ========================================================
      // SYNCHRONIZE RELATED PRODUCTS & BROADCAST EVENT
      // ========================================================

      if (editingId !== null) {
        const oldCatObj = categories.find((c) => Number(c.id) === Number(editingId));
        const previousName = editingOriginalName || oldCatObj?.category || "";
        await syncCategoryProductsInInventory(previousName, categoryName.trim());
        setMessage("Category and related inventory products updated successfully.");
      } else {
        await syncCategoryProductsInInventory(categoryName.trim(), categoryName.trim());
        setMessage("Category added successfully.");
      }

      try {
        localStorage.setItem("vanya_category_sync", String(Date.now()));
        window.dispatchEvent(new Event("vanya_category_sync"));
      } catch (e) {
        // ignore storage errors
      }

      // ========================================================
      // CLEAR FORM
      // ========================================================

      setCategoryName("");
      setImageFile(null);
      setImagePreview("");
      setEditingId(null);
      setEditingOriginalName("");

      const fileInput = document.getElementById(
        "category-image-input"
      );

      if (fileInput) {
        fileInput.value = "";
      }

      // ========================================================
      // REFRESH CATEGORY LIST
      // ========================================================

      await fetchCategories();
    } catch (error) {
      console.error(
        editingId !== null
          ? "UPDATE CATEGORY ERROR:"
          : "ADD CATEGORY ERROR:",
        error
      );

      setMessage(
        error.message ||
          (editingId !== null
            ? "Failed to update category."
            : "Failed to add category.")
      );
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // EDIT CATEGORY
  // ============================================================

  const handleEdit = (category) => {
    if (!category?.id) {
      setMessage("Category ID not found.");
      return;
    }

    // Store ID so Save knows this is UPDATE
    setEditingId(category.id);
    setEditingOriginalName(category.category || "");

    // Load category name
    setCategoryName(
      category.category || ""
    );

    // Show existing Cloudinary image
    setImagePreview(
      category.image_url || ""
    );

    // Important:
    // Do not set existing URL as imageFile.
    // User only needs to select a new file if
    // they want to replace the image.
    setImageFile(null);

    setMessage("");

    // Clear file input
    const fileInput = document.getElementById(
      "category-image-input"
    );

    if (fileInput) {
      fileInput.value = "";
    }

    // Scroll to form
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // ============================================================
  // DELETE CATEGORY
  // ============================================================

  const handleDelete = async (category) => {
    if (!category?.id) {
      setMessage("Category ID not found.");
      return;
    }

    const confirmed = window.confirm(
      `Delete "${category.category}" category?`
    );

    if (!confirmed) return;

    try {
      setMessage("");

      let response = await fetch(
        `${COLLECTION_API}/delete/${category.id}`,
        {
          method: "DELETE",
        }
      );

      if (response.status === 404) {
        response = await fetch(
          `${COLLECTION_API}/${category.id}`,
          {
            method: "DELETE",
          }
        );
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Failed to delete category"
        );
      }

      setMessage(
        "Category deleted successfully."
      );

      // If deleted category was being edited
      if (editingId === category.id) {
        handleClear();
      }

      await fetchCategories();
    } catch (error) {
      console.error(
        "DELETE CATEGORY ERROR:",
        error
      );

      setMessage(
        error.message ||
          "Failed to delete category."
      );
    }
  };

  // ============================================================
  // CLEAR FORM
  // ============================================================

  const handleClear = () => {
    setCategoryName("");
    setImageFile(null);
    setImagePreview("");
    setEditingId(null);
    setEditingOriginalName("");
    setMessage("");

    const fileInput = document.getElementById(
      "category-image-input"
    );

    if (fileInput) {
      fileInput.value = "";
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loading}>
          Loading categories...
        </div>
      </div>
    );
  }

  // ============================================================
  // UI
  // ============================================================

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* =====================================================
            HEADER
        ====================================================== */}

        <div style={styles.header}>
          <p style={styles.smallTitle}>
            VANYA COLLECTIONS
          </p>

          <h1 style={styles.title}>
            Category Management
          </h1>

          <p style={styles.description}>
            Create and manage collection categories.
          </p>
        </div>

        {/* =====================================================
            ADD / EDIT CATEGORY FORM
        ====================================================== */}

        <div className="category-form-card" style={styles.formCard}>

          <h2 style={styles.sectionTitle}>
            {editingId !== null
              ? "Edit Category"
              : "Add Category"}
          </h2>

          {/* EDIT MODE INFO */}

          {editingId !== null && (
            <div style={styles.editModeBox}>
              <strong>
                Editing Category
              </strong>

              <span>
                ID: {editingId}
              </span>
            </div>
          )}

          {/* ===================================================
              CATEGORY NAME
          ==================================================== */}

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Category Name
            </label>

            <input
              type="text"
              value={categoryName}
              onChange={(e) =>
                setCategoryName(e.target.value)
              }
              placeholder="Enter category name"
              style={styles.input}
              disabled={saving}
            />
          </div>

          {/* ===================================================
              IMAGE
          ==================================================== */}

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Category Image
            </label>

            <label
              htmlFor="category-image-input"
              style={styles.uploadBox}
            >
              <div style={styles.uploadIcon}>
                +
              </div>

              <strong style={styles.uploadTitle}>
                {editingId !== null
                  ? "Choose New Image"
                  : "Upload Image"}
              </strong>

              <span style={styles.uploadText}>
                {editingId !== null
                  ? "Leave empty to keep the current image"
                  : "Click here to choose an image"}
              </span>
            </label>

            <input
              id="category-image-input"
              type="file"
              onChange={handleImageChange}
              style={{
                display: "none",
              }}
              disabled={saving}
            />
          </div>

          {/* ===================================================
              SELECTED FILE
          ==================================================== */}

          {imageFile && (
            <div style={styles.selectedFile}>
              <strong>
                Selected Image:
              </strong>{" "}
              {imageFile.name}
            </div>
          )}

          {/* ===================================================
              IMAGE PREVIEW
          ==================================================== */}

          {imagePreview && (
            <div style={styles.previewSection}>

              <label style={styles.label}>
                {editingId !== null &&
                !imageFile
                  ? "Current Image"
                  : "Preview"}
              </label>

              <div style={styles.previewWrapper}>
                <img
                  src={imagePreview}
                  alt="Category Preview"
                  style={styles.previewImage}
                />
              </div>

            </div>
          )}

          {/* ===================================================
              BUTTONS
          ==================================================== */}

          <div style={styles.buttonRow}>

            {/* CLEAR */}

            <button
              type="button"
              onClick={handleClear}
              disabled={saving}
              style={{
                ...styles.clearButton,
                opacity: saving ? 0.6 : 1,
              }}
            >
              {editingId !== null
                ? "Cancel Edit"
                : "Clear"}
            </button>

            {/* ADD / UPDATE */}

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                ...styles.saveButton,
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving
                ? editingId !== null
                  ? "Updating..."
                  : "Adding..."
                : editingId !== null
                ? "Update Category"
                : "Add Category"}
            </button>

          </div>

          {/* ===================================================
              MESSAGE
          ==================================================== */}

          {message && (
            <div style={styles.message}>
              {message}
            </div>
          )}

        </div>

        {/* =====================================================
            CATEGORY LIST
        ====================================================== */}

        <div className="category-list-card" style={styles.listSection}>

          <div style={styles.listHeader}>

            <div>
              <h2 style={styles.sectionTitle}>
                Categories
              </h2>

              <p style={styles.listDescription}>
                Manage your existing collection
                categories.
              </p>
            </div>

            <div style={styles.countBadge}>
              {categories.length}
            </div>

          </div>

          {/* ===================================================
              EMPTY
          ==================================================== */}

          {categories.length === 0 ? (
            <div style={styles.empty}>
              No categories added yet.
            </div>
          ) : (

            <div className="category-grid-responsive" style={styles.grid}>

              {categories.map((category) => (

                <div
                  key={category.id}
                  style={{
                    ...styles.categoryCard,

                    // Highlight category currently
                    // being edited
                    border:
                      editingId === category.id
                        ? "2px solid #e2bc53"
                        : "1px solid #eadcf0",
                  }}
                >

                  {/* =================================================
                      IMAGE
                  ================================================== */}

                  <div
                    style={
                      styles.cardImageWrapper
                    }
                  >

                    <img
                      src={category.image_url}
                      alt={category.category}
                      style={styles.cardImage}
                      onError={(e) => {
                        e.currentTarget.style.display =
                          "none";
                      }}
                    />

                  </div>

                  {/* =================================================
                      DETAILS
                  ================================================== */}

                  <div style={styles.cardContent}>

                    <h3 style={styles.cardTitle}>
                      {category.category}
                    </h3>

                    <div
                      style={styles.cardActions}
                    >

                      {/* EDIT */}

                      <button
                        type="button"
                        onClick={() =>
                          handleEdit(category)
                        }
                        disabled={saving}
                        style={{
                          ...styles.editButton,
                          opacity: saving
                            ? 0.6
                            : 1,
                        }}
                      >
                        Edit
                      </button>

                      {/* DELETE */}

                      <button
                        type="button"
                        onClick={() =>
                          handleDelete(category)
                        }
                        disabled={saving}
                        style={{
                          ...styles.deleteButton,
                          opacity: saving
                            ? 0.6
                            : 1,
                        }}
                      >
                        Delete
                      </button>

                    </div>

                  </div>

                </div>

              ))}

            </div>

          )}

        </div>

      </div>

      <style>{`
        @media (max-width: 768px) {
          .category-form-card,
          .category-list-card {
            padding: 18px 14px !important;
            border-radius: 14px !important;
          }
          .category-grid-responsive {
            grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)) !important;
            gap: 12px !important;
          }
        }
      `}</style>
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "transparent",
    padding: "10px 0 30px",
    boxSizing: "border-box",
    fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
  },

  container: {
    maxWidth: "1200px",
    margin: "0 auto",
  },

  loading: {
    minHeight: "70vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#14352b",
    fontSize: "16px",
    fontWeight: "600",
  },

  header: {
    marginBottom: "26px",
  },

  smallTitle: {
    color: "#D4AF37",
    fontSize: "11px",
    letterSpacing: "1.5px",
    fontWeight: "700",
    margin: "0 0 6px",
    textTransform: "uppercase",
  },

  title: {
    color: "#14352b",
    fontSize: "26px",
    fontWeight: "800",
    margin: "0 0 6px",
  },

  description: {
    color: "#64748b",
    fontSize: "14px",
    margin: 0,
  },

  formCard: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "28px",
    marginBottom: "32px",
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.04)",
    border: "1px solid #e2e8f0",
  },

  sectionTitle: {
    color: "#14352b",
    fontSize: "18px",
    fontWeight: "800",
    margin: "0 0 18px",
  },

  editModeBox: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "20px",
    padding: "12px 16px",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "10px",
    color: "#92400e",
    fontSize: "13px",
    fontWeight: "600",
  },

  formGroup: {
    marginBottom: "20px",
  },

  label: {
    display: "block",
    color: "#334155",
    fontSize: "13px",
    fontWeight: "700",
    marginBottom: "6px",
  },

  input: {
    width: "100%",
    height: "46px",
    padding: "0 14px",
    border: "1.5px solid #cbd5e1",
    borderRadius: "10px",
    outline: "none",
    fontSize: "14px",
    color: "#0f172a",
    background: "#fff",
    boxSizing: "border-box",
  },

  uploadBox: {
    minHeight: "130px",
    border: "2px dashed #cbd5e1",
    borderRadius: "12px",
    background: "#f8fafc",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    textAlign: "center",
    padding: "20px",
  },

  uploadIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    background: "#14352b",
    color: "#D4AF37",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
    marginBottom: "8px",
  },

  uploadTitle: {
    color: "#14352b",
    fontSize: "14px",
    fontWeight: "700",
  },

  uploadText: {
    color: "#64748b",
    fontSize: "12px",
    marginTop: "4px",
  },

  selectedFile: {
    marginTop: "-6px",
    marginBottom: "18px",
    padding: "8px 12px",
    background: "#ecfdf5",
    borderRadius: "8px",
    color: "#065f46",
    fontSize: "12px",
    fontWeight: "600",
  },

  previewSection: {
    marginBottom: "20px",
  },

  previewWrapper: {
    width: "200px",
    height: "200px",
    borderRadius: "12px",
    overflow: "hidden",
    border: "3px solid #D4AF37",
    background: "#f1f5f9",
  },

  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  buttonRow: {
    display: "flex",
    gap: "10px",
  },

  clearButton: {
    border: "1.5px solid #cbd5e1",
    borderRadius: "10px",
    padding: "12px 22px",
    background: "#ffffff",
    color: "#475569",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "14px",
  },

  saveButton: {
    border: "none",
    borderRadius: "10px",
    padding: "12px 26px",
    background: "linear-gradient(135deg, #14352b 0%, #1f4a3c 100%)",
    color: "#D4AF37",
    fontWeight: "800",
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(20, 53, 43, 0.25)",
    fontSize: "14px",
  },

  message: {
    marginTop: "14px",
    padding: "12px 16px",
    background: "#ecfdf5",
    borderRadius: "10px",
    border: "1px solid #a7f3d0",
    color: "#065f46",
    fontSize: "13px",
    fontWeight: "600",
  },

  listSection: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "28px",
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.04)",
    border: "1px solid #e2e8f0",
  },

  listHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
  },

  listDescription: {
    color: "#64748b",
    fontSize: "13px",
    margin: "-8px 0 0",
  },

  countBadge: {
    background: "#14352b",
    color: "#D4AF37",
    width: "34px",
    height: "34px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "800",
    fontSize: "13px",
  },

  empty: {
    padding: "45px 20px",
    textAlign: "center",
    color: "#64748b",
    background: "#f8fafc",
    borderRadius: "12px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
    gap: "20px",
  },

  categoryCard: {
    background: "#ffffff",
    borderRadius: "14px",
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },

  cardImageWrapper: {
    width: "100%",
    height: "200px",
    overflow: "hidden",
    background: "#f1f5f9",
  },

  cardImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  cardContent: {
    padding: "16px",
  },

  cardTitle: {
    color: "#14352b",
    fontSize: "16px",
    fontWeight: "700",
    margin: "0 0 12px",
  },

  cardActions: {
    display: "flex",
    gap: "8px",
  },

  editButton: {
    flex: 1,
    border: "1px solid #14352b",
    background: "#14352b",
    color: "#D4AF37",
    borderRadius: "8px",
    padding: "8px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "12.5px",
  },

  deleteButton: {
    flex: 1,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#dc2626",
    borderRadius: "8px",
    padding: "8px",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "12.5px",
  },
};

export default CategoryManagement;
